import { LoadingOutlined, InboxOutlined } from '@ant-design/icons';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageEnum, TABLEPAI } from '@/enums/pageEnum';
import { Form, Tag, Button, Collapse, Radio, Spin } from 'antd';
import { Phone, ErrorImg, Email } from '@/assets';
import { BasicTable, CustomModal } from '@/components';
import { debounce } from 'lodash-es';
import { useAppSelector } from '@/store';
import CaseLog from './CaseLog';
import NotificationLog from './NotificationLog';
import CallLog from './CallLog';
import BulkyCase, { type BulkyCaseData } from './BulkyCase';
import useMessageRedux from '@/store/message-redux';
import { getTimeToString } from '@/utils/toolt';
import { GetLiftStatusCols, GetExpandIcon } from '@/utils/commonConfig';
import NProgress from 'nprogress';
import {
  ProFormSelect,
  ProFormText,
  ProForm,
  PageContainer,
  ProFormTextArea,
  ProFormDigit,
} from '@ant-design/pro-components';
import { useState, useMemo, useCallback, useEffect, useTransition } from 'react';
import {
  creatCaseManagement,
  searchLocation,
  getCaseDetails,
  updateCaseManage,
  getDetailInfo,
} from '@api';
import {
  mediaOptions,
  priorityOptions,
  subcategoryOptions,
  feedbackOptions,
  residOptions,
  categoryOptions,
} from '../constant';

export default function CaseManageDetailPage() {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const [fetching, startfetching] = useTransition();
  const { id } = useParams();
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const darkMode = useAppSelector((state) => state.app.theme);
  const TABKEY = 'caseDetails';
  const [tabActiveKey, setTabActiveKey] = useState<string>(TABKEY);
  const [searchType, setSearchType] = useState<string>('address');
  const [location, setLocation] = useState<any>(null);
  const [optionsLocation, setOptionsLocation] = useState<any[]>([]);
  const { message } = useMessageRedux();
  const [collapseActiveKey, setCollapseActiveKey] = useState<number>();
  const [bulkyCase, setBulkyCase] = useState<BulkyCaseData | null>(null);

  const isEdit = useMemo(() => {
    return pathname.includes(PageEnum.CASE_MANAGDETAL);
  }, [pathname]);

  const isAddNew = useMemo(() => {
    return pathname === PageEnum.CASE_MANAGDEADD;
  }, [pathname]);

  const tabKeys = useMemo(() => {
    if (isAddNew) return [];
    return [
      { tab: t('caseDetails'), key: TABKEY },
      { tab: t('caseLog'), key: 'caseLog' },
      { tab: t('notifiLog'), key: 'notifiLog' },
      { tab: t('callLog'), key: 'callLog' },
    ];
  }, [TABKEY, t, isAddNew]);

  const personChargeList = useMemo(() => {
    return [
      {
        name: 'ANG WAI KIAN',
        phone: '88888888',
        email: 'iems@test.com',
        type: 'PM',
      },
      { name: 'AKPO', phone: '88888888', email: 'iems@test.com', type: 'PO' },
      {
        name: 'NG JING KAI',
        phone: '88888888',
        email: 'iems@test.com',
        type: 'MP',
      },
    ];
  }, []);

  const liftStatusCols = useMemo(() => {
    return GetLiftStatusCols(t);
  }, [t]);

  const getLabel = useCallback(
    (name: string, tipro?: string) => {
      return (
        <div>
          <p className="flex justify-start gap-1">
            <span>{t(name)}</span>
            <span className="text-[#f00] text-[18px]">*</span>
          </p>
          {tipro && (
            <p className="text-[#f00]">
              <img src={ErrorImg} className="float-left mr-0.5" />
              <span>{tipro}</span>
            </p>
          )}
        </div>
      );
    },
    [t],
  );

  const iframeSrc = useMemo(() => {
    const style = darkMode === 'dark' ? 'Night' : 'Default';
    const baseUrl = `https://www.onemap.gov.sg/amm/amm.html?mapStyle=${style}`;
    if (!location) {
      return `${baseUrl}&zoomLevel=12`;
    }
    return `${baseUrl}&zoomLevel=17&marker=postalcode:${
      location.attribute?.postalCode || location.buildingPostcode
    }!iwt:JTNDcCUzRVdlJTIwYXJlJTIwaGVyZSElM0MlMkZwJTNF!colour:blue&popupWidth=120`;
  }, [location, darkMode]);

  const creatCaseDetails = useCallback(
    (values: any) => {
      if (!location || !location?.liftId) {
        return message.warning('Please select lift.');
      }
      startTransition(async () => {
        try {
          const res = id
            ? await updateCaseManage({ id, ...values })
            : await creatCaseManagement({
                ...values,
                liftId: location?.liftId,
              });
          message.success('Success');
          if (res.status === 200) {
            navigation(`${PageEnum.CASE_MANAGDETAL}/${res.data.body || id}`);
          }
        } catch (error: any) {
          message.error(error?.detail || error);
        }
      });
    },
    [message, location, id, navigation],
  );

  const getModalLocation = useCallback(
    (value: string) => {
      startfetching(async () => {
        const res = await searchLocation({ [searchType]: value });
        setOptionsLocation(
          res.data.body?.map((item: any) => ({
            ...item,
            address: `${item.attribute?.address}, ${item.attribute?.postalCode}`,
          })),
        );
      });
    },
    [searchType],
  );

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => getModalLocation(value);
    return debounce(loadOptions, 500);
  }, [getModalLocation]);

  const getDetailById = useCallback(() => {
    if (!id) return;
    startTransition(async () => {
      try {
        const res = await getCaseDetails(id);
        if (res.data.body?.category === 'BULKY_ITEM_REMOVAL') {
          // not a lift case: no building / lift status to load
          setBulkyCase(res.data.body);
          setLocation(res.data.body);
          return;
        }
        const info = await getDetailInfo(`building/${res.data.body?.buildingId}`);
        const body = res.data.body;
        const buildInfo = info.data.body;
        setLocation({ ...buildInfo, ...body, tc: buildInfo?.attribute?.tc });
        setCollapseActiveKey(1);
        form.setFieldsValue({
          ...body,
          tc: buildInfo?.attribute?.tc,
          locatAddr: `${body.buildingAddress}, ${body.buildingPostcode}`,
        });
      } catch (error: any) {
        message.error(error?.detail);
      }
    });
  }, [id, message, form]);

  const getAddresLocation = useMemo(() => {
    const label = (
      <div className="pb-3">
        <Radio.Group
          value={searchType}
          onChange={(e) => {
            setSearchType(e.target.value);
            setLocation(null);
            form.setFieldsValue({ tc: null, location: null });
            setCollapseActiveKey(undefined);
          }}
        >
          <Radio value="address">{t('Block')}</Radio>
          <Radio value="postcode">{t('postCode')}</Radio>
        </Radio.Group>
      </div>
    );
    if (!isAddNew) return <ProFormText label={label} name="locatAddr" />;
    return (
      <ProFormSelect.SearchSelect
        label={label}
        rules={[{ required: true, message: t('hint.pleLoc') }]}
        fieldProps={{
          allowClear: false,
          maxCount: 1,
          onSearch: debounceFetcher,
          listHeight: 200,
          notFoundContent: (
            <div className="flex flex-col justify-center items-center text-[#eee] h-16">
              {fetching ? (
                <Spin />
              ) : (
                <>
                  <p>
                    <InboxOutlined style={{ fontSize: '40px' }} />
                  </p>
                  <p>No data</p>
                </>
              )}
            </div>
          ),
          fieldNames: { label: 'address', value: 'id' },
        }}
        name="location"
        onChange={(val: any[]) => {
          setLocation(val.length > 0 ? val[0] : null);
          form.setFieldValue('tc', val.length > 0 ? val[0]?.attribute?.tc : '');
          setCollapseActiveKey(val.length);
        }}
        options={optionsLocation}
      />
    );
  }, [debounceFetcher, optionsLocation, searchType, t, isAddNew, form, fetching]);

  useEffect(() => {
    setTimeout(() => {
      NProgress.done();
    }, 600);
  }, [pathname]);

  useEffect(() => {
    getDetailById();
  }, [getDetailById]);

  const getPhoneInput = useCallback(
    (name: string, label?: string) => {
      return (
        <ProFormDigit
          addonBefore="+65"
          label={t(label || name)}
          disabled={isEdit}
          name={name}
          placeholder={''}
          fieldProps={{ maxLength: 8, controls: false, changeOnWheel: false }}
          rules={[{ pattern: /^\d{8}$/, message: t('hint.phoneNum') }]}
        />
      );
    },
    [isEdit, t],
  );

  return (
    <Spin spinning={isPending} indicator={<LoadingOutlined spin />}>
      <div className="bg-[#f2f2f2] dark:bg-[#000] typeDetailCalss caseDetailCass">
        <PageContainer
          tabList={tabKeys}
          tabActiveKey={tabActiveKey}
          onTabChange={(key) => setTabActiveKey(key)}
          header={{
            title: null,
            breadcrumb: {
              items: [
                {
                  title: <Link to={PageEnum.CASE_MANAGDE}>{t('case_management')}</Link>,
                },
                {
                  title: isAddNew ? t('newCase') : t('caseDetails'),
                },
              ],
            },
          }}
        />
        {tabActiveKey === TABKEY && bulkyCase && (
          <BulkyCase data={bulkyCase} onChanged={getDetailById} />
        )}
        {tabActiveKey === TABKEY && !bulkyCase && (
          <div className="p-4 flex justify-between gap-4">
            <div className="flex-1">
              <ProForm
                layout={'vertical'}
                form={form}
                initialValues={{
                  feedbackType: 'FEEDBACK',
                  category: 'LIFT_ESCALATOR',
                  channel: 'RESIDENCE',
                  media: 'PHONE',
                  priority: 'IMMEDIATE',
                }}
                className="formDisabledColor"
                disabled={!isAddNew}
                submitter={false}
                onFinish={(values) => {
                  creatCaseDetails(values);
                }}
              >
                <div className="bg-[#fff] dark:bg-[#141414] caseLocationDiv p-4 mb-4">
                  {!isAddNew && (
                    <div className="pb-4 mb-5 border-b-[1px] leading-5 border-[#E8E9ED] dark:border-[#ffffff21]">
                      <span className="text-[20px] font-bold mr-10">
                        {t('caseID')}: {location?.caseNo}
                      </span>
                      <span>
                        {t('createdDate')}: {getTimeToString(location?.createdAt)}
                      </span>
                    </div>
                  )}
                  <div className="text-[20px] font-medium mb-5">{t('caseLocat')}</div>
                  <div className="flex justify-between gap-8">
                    <div className="flex-1">{getAddresLocation}</div>
                    <div className="flex-1 pt-3">
                      <ProFormSelect disabled label={t('townCouncil')} name="tc" />
                    </div>
                  </div>
                  <div className="flex justify-between gap-8">
                    <div className="flex-1">
                      <ProFormText label={t('floor')} name="floor" placeholder="" />
                    </div>
                    <div className="flex-1">
                      <ProFormText label={t('unit')} name="unit" placeholder="" />
                    </div>
                    {isAddNew && (
                      <div className="w-[200px] text-right pt-[30px]">
                        <CustomModal
                          title={t('feedbackHistory')}
                          isProForm={false}
                          width={990}
                          buttonCogfig={{ disabled: !location }}
                          className="feedbackHistoryModal"
                          isShowOkButton={false}
                        >
                          <BasicTable
                            columns={[
                              { title: t('caseID'), dataIndex: 'caseNo' },
                              {
                                title: t('loggedDate'),
                                dataIndex: 'createdAt',
                                renderText: (text: number) => getTimeToString(text),
                              },
                              {
                                title: t('category'),
                                dataIndex: 'categoryFormat',
                              },
                              {
                                title: t('subcategory'),
                                dataIndex: 'subcategoryFormat',
                              },
                              {
                                title: t('caseLocKey'),
                                dataIndex: 'buildingAddress',
                              },
                              {
                                title: t('description'),
                                dataIndex: 'description',
                              },
                              {
                                title: t('complainant'),
                                dataIndex: 'complainant',
                              },
                            ]}
                            filterTableFields={[
                              {
                                field: 'lift',
                                label: t('liftNumber'),
                                component: 'text',
                                fieldProps: { style: { width: '115px' } },
                              },
                              {
                                field: 'floor',
                                label: t('floor'),
                                component: 'text',
                                fieldProps: { style: { width: '115px' } },
                              },
                              {
                                field: 'unit',
                                label: t('unit'),
                                component: 'text',
                                fieldProps: { style: { width: '115px' } },
                              },
                              {
                                field: 'feedbackType',
                                label: t('feedbackType'),
                                component: 'select',
                                options: feedbackOptions,
                                fieldProps: { style: { width: '130px' } },
                              },
                            ]}
                            isAddParamsToUrl={false}
                            isPagination={false}
                            isShowCheckbox={false}
                            isDefaultToolBar={false}
                            dataKey="data.body"
                            className="min-h-max !px-0"
                            requestUrl={`${TABLEPAI.CASE_FEEDBACK}?buildingId=${location?.buildingId}`}
                          />
                        </CustomModal>
                      </div>
                    )}
                  </div>
                  <p className="h-[1px] bg-[#E8E9ED] dark:bg-[#ffffff21] w-full"></p>
                  <Collapse
                    activeKey={collapseActiveKey}
                    className="mt-7"
                    expandIconPosition="end"
                    expandIcon={({ isActive }) => GetExpandIcon(isActive, 16)}
                    onChange={() => {
                      setCollapseActiveKey(!collapseActiveKey ? 1 : undefined);
                    }}
                    items={[
                      {
                        key: 1,
                        label: (
                          <h3 className="text-[16px] font-medium">{t('liftStatus')}</h3>
                        ),
                        children: location ? (
                          <div>
                            <BasicTable
                              columns={liftStatusCols}
                              rowSelection={{ type: 'radio' }}
                              isDefaultToolBar={false}
                              isPagination={false}
                              rowKey={(record: any) => record?.lift?.id}
                              tableHeight={240}
                              className="!p-0 min-h-max liftStatusCalss"
                              dataKey="data.body"
                              requestUrl={`${TABLEPAI.CASE_LIFT_STATUS}?buildingId=${location?.buildingId}`}
                              longPolling={isAddNew}
                              isRefreshSelectedRows={false}
                              selectRowKeys={[location?.liftId]}
                              getCurrentTable={(data: any) => {
                                if (isAddNew || location?.liftCompany) return;
                                const currentLift = data?.find(
                                  (item: any) => item?.lift?.id === location?.liftId,
                                );
                                if (currentLift) {
                                  setLocation((pev: any) => ({
                                    ...pev,
                                    liftCompany:
                                      currentLift.lift?.attribute?.maintenanceCompany,
                                  }));
                                }
                              }}
                              onChangeSelected={(rowKeys, selectedRows) => {
                                if (!rowKeys.length) return;
                                setLocation((pev: any) => ({
                                  ...pev,
                                  liftId: rowKeys[0],
                                  liftCompany:
                                    selectedRows[0]?.lift?.attribute?.maintenanceCompany,
                                }));
                              }}
                            />
                            <p className="text-center py-4">
                              {t('LIFT_COMPANY')}: {location?.liftCompany} (Tel: 67491188)
                            </p>
                          </div>
                        ) : null,
                      },
                    ]}
                  />
                </div>
                <div className="bg-[#fff] dark:bg-[#141414] p-4 pb-1 mb-4">
                  <div className="text-[20px] font-medium mb-5">{t('caseDetails')}</div>
                  <div className="flex justify-between gap-16">
                    <div className="flex-1">
                      <ProFormSelect
                        label={getLabel('feedbackType')}
                        rules={[
                          {
                            required: true,
                            message: 'Please select feedback type.',
                          },
                        ]}
                        name="feedbackType"
                        options={feedbackOptions}
                      />
                    </div>
                    <div className="flex-1">
                      <ProFormSelect
                        label={getLabel('priorityT')}
                        name="priority"
                        rules={[
                          {
                            required: true,
                            message: 'Please select priority.',
                          },
                        ]}
                        options={priorityOptions}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between gap-16">
                    <div className="flex-1">
                      <ProFormSelect
                        label={getLabel('category')}
                        rules={[
                          {
                            required: true,
                            message: 'Please select category.',
                          },
                        ]}
                        name="category"
                        options={categoryOptions}
                      />
                    </div>
                    <div className="flex-1">
                      <ProFormSelect
                        label={getLabel('subcategory')}
                        rules={[
                          {
                            required: true,
                            message: 'Please select subcategory.',
                          },
                        ]}
                        name="subcategory"
                        options={subcategoryOptions}
                      />
                    </div>
                  </div>
                  <ProFormTextArea
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: 'Please input defect description.',
                      },
                    ]}
                    label={getLabel('defectDescr', t('hint.personInfor'))}
                  />
                </div>
                <div className="bg-[#fff] dark:bg-[#141414] p-4 complaiDetail">
                  <div className="text-[20px] font-medium mb-5">{t('compDetail')}</div>
                  <div className="flex justify-between gap-16 mb-6">
                    <div className="flex-1">
                      <ProFormSelect
                        label={t('channel')}
                        name="channel"
                        disabled={isEdit}
                        fieldProps={{ allowClear: false }}
                        options={residOptions}
                      />
                    </div>
                    <div className="flex-1">
                      <ProFormSelect
                        label={t('media')}
                        name="media"
                        fieldProps={{ allowClear: false }}
                        disabled={isEdit}
                        options={mediaOptions}
                      />
                    </div>
                  </div>
                  <ProFormText
                    name="complainant"
                    label={t('name')}
                    disabled={isEdit}
                    placeholder={''}
                  />
                  <div className="flex justify-between gap-16 residentItem">
                    {getPhoneInput('residential')}
                    {getPhoneInput('phone', 'handphone')}
                  </div>
                  <ProFormText
                    name="email"
                    label={t('email')}
                    disabled={isEdit}
                    rules={[{ required: false, type: 'email' }]}
                    placeholder={''}
                  />
                  <ProFormText
                    name="complainantAddress"
                    disabled={isEdit}
                    label={t('compAdderss')}
                    placeholder={''}
                  />
                </div>
                <div className="bg-[#fff] dark:bg-[#141414] p-4 text-right sticky bottom-0">
                  <Button
                    disabled={false}
                    variant="outlined"
                    onClick={() => {
                      navigation(PageEnum.CASE_MANAGDE);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  {location?.status !== 'CLOSED' && (
                    <Button
                      type="primary"
                      disabled={false}
                      className="pr-4 pl-4 ml-3"
                      loading={isPending}
                      onClick={() => {
                        if (isEdit) {
                          navigation(`${PageEnum.CASE_MANAGDEDETAL}/${id}`);
                          return;
                        }
                        form?.submit?.();
                      }}
                    >
                      {isEdit ? t('edit') : t('save')}
                    </Button>
                  )}
                </div>
              </ProForm>
            </div>
            {/* left */}
            <div className="bg-[#fff] dark:bg-[#141414] w-[410px] p-3 rounded-[2px]">
              <div>
                <div className="text-[20px] font-medium mb-5">{t('caseLocation')}</div>
                <iframe src={iframeSrc} height="360" width="100%" />
              </div>
              <div className="mt-8">
                <div className="text-[20px] font-medium mb-5">{t('personCharge')}</div>
                {personChargeList.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="border-[1px] border-[#E8E9ED] dark:border-[#ffffff21] px-3 leading-7 mb-2.5"
                    >
                      <p className="py-2">
                        <Tag className="border-[#008000] text-[#008000] font-medium bg-transparent rounded-full">
                          {item.type}
                        </Tag>
                        <span className="text-[16px] font-medium text-[#000000d9] dark:text-[#ffffffd9]">
                          {item.name}
                        </span>
                      </p>
                      <p className="h-[1px] bg-[#E8E9ED] dark:bg-[#ffffff21] w-full"></p>
                      <p className="pt-1.5">
                        <img src={Phone} className="float-left pt-1 mr-1" />
                        <span>{item.phone}</span>
                      </p>
                      <p className="pb-1.5">
                        <img src={Email} className="float-left pt-1.5 mr-1" />
                        <span>{item.email}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <div className="text-[20px] font-medium">{t('buildingInfor')}</div>
                <div className="border-[1px] border-[#E8E9ED] dark:border-[#ffffff21] p-3 mt-5">
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('buildingDate')}:</span>
                    <span>{location?.attribute?.issInstalledDate}</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('buildingType')}:</span>
                    <span>HDB Residential</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('hdbBranc')}:</span>
                    <span>{location?.attribute?.branchOffice}</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">EMC:</span>
                    <span>{location?.attribute?.estateMgmtCo}</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('ward')}:</span>
                    <span>{location?.attribute?.ward}</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('totalUnit')}: </span>
                    <span>{location?.attribute?.totalUnit}</span>
                  </div>
                  <div className="flex justify-start mt-3">
                    <span className="w-[132px]">{t('totalFloor')}: </span>
                    <span>{location?.attribute?.totalFloor}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {tabActiveKey === 'caseLog' && <CaseLog />}
        {tabActiveKey === 'notifiLog' && (
          <NotificationLog
            bulky={!!bulkyCase}
            notifications={bulkyCase?.details?.notifications}
          />
        )}
        {tabActiveKey === 'callLog' && <CallLog />}
      </div>
    </Spin>
  );
}
